/* eslint-disable jsx-a11y/anchor-has-content */
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'components/UI/button/button'
import Input from 'components/UI/input'
import { MaskedInput } from 'components/UI/input/masked-input'
import Modal from 'components/UI/modal/modal'
import SubBody from 'components/UI/sub-body/sub-body'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { api } from 'redux/api'
import { useApplyMutation, useGetPrepaidGeneralInfoQuery } from 'redux/api/buy-card.api'
import { useOtpVerifyMutation } from 'redux/api/otp.api'
import tw from 'twin.macro'
import * as yup from 'yup'

import { createPhoneSchema } from '../../share/schemas/phone.schema'
import AcceptDenominationChoice from './accept-denomination-choice'
import CardlimitContainer from './card-limit-container'
import OtpVerify from './otp-verify'

function BuyCardModal({
  open,
  onClose,
  isBuyCard,
  denomination,
  channel
}: {
  open: boolean
  onClose: () => void
  isBuyCard?: boolean
  denomination?: { amount: string; product_code: string }
  channel: 'landing' | 'auth' | 'cabinet'
}) {
  const [otpOpen, setOtpOpen] = useState<boolean>(false)
  const [activeTitle, setActiveTitle] = useState<string | null>()
  const [cardLimit, setCardLimit] = useState<boolean>(false)
  const { t, i18n } = useTranslation()
  const phoneSchema = useMemo(() => createPhoneSchema(t), [i18n.language])

  const {
    control,
    formState: { isValid },
    getValues,
    setError,
    watch,
    reset
  } = useForm({ defaultValues: { mobile_phone: '', code: '' }, resolver: yupResolver(phoneSchema) })

  const [apply, { isSuccess: applySuccess, error: applyError, isLoading: applyLoading, data }] = useApplyMutation()
  const [otpVerify, { isSuccess: verifySuccess, isLoading: otpVerifyLoading }] = useOtpVerifyMutation()
  const { data: prepaidGeneralInfo } = useGetPrepaidGeneralInfoQuery(null)

  const handleSendOtp = async () => {
    try {
      const mobile_phone = getValues().mobile_phone.replace(/[()\s]/g, '')
      if (isBuyCard) {
        await apply({ mobile_phone }).unwrap()
      }
    } catch (error) {
      const typedErr = error as {
        data: {
          error: { description: string | { mobile_phone: string[] }; code: string }
        }
        status: number
      }
      if (
        typedErr.data.error.code === 'UNFINISHED_APPLICATION' &&
        typeof typedErr.data.error.description === 'string'
      ) {
        setError('mobile_phone', { type: 'validate', message: typedErr.data.error.description })
      } else {
        if (typeof typedErr.data.error.description === 'string') {
          if (typedErr.data.error.code) {
            setOtpOpen(true)
            setError('code', { type: 'validate', message: typedErr.data.error.description })
          }
          setError('mobile_phone', { type: 'validate', message: typedErr.data.error.description })
        } else if (typedErr.data.error.description?.mobile_phone) {
          setError('mobile_phone', {
            type: 'validate',
            message: typedErr.data.error.description.mobile_phone[0]
          })
        }
      }

      if (typedErr.data.error.code === 'LIMIT_CARD_ERROR') {
        setCardLimit(true)
      }
    }
  }

  const handleOtpVerify = async () => {
    try {
      await otpVerify({ mobile_phone: watch('mobile_phone').replace(/[()\s]/g, ''), code: watch('code') }).unwrap()
    } catch (error) {
      const typedErr = error as {
        data: {
          error: { description: string; code: string }
        }
        status: number
      }

      if (typedErr.data.error.description) {
        setError('code', { type: 'validate', message: typedErr.data.error.description })
      }
    }
  }
  useEffect(() => {
    if (verifySuccess || cardLimit) {
      setActiveTitle('')
    } else if (otpOpen && !verifySuccess) {
      setActiveTitle(t('modal_title', { ns: 'otp' }))
    } else {
      setActiveTitle(t('title', { ns: 'auth' }))
    }
  }, [otpOpen, verifySuccess, t, cardLimit])

  useEffect(() => {
    if (open && channel === 'cabinet') {
      handleSendOtp()
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      setCardLimit(false)
      reset()
      setOtpOpen(false)
    }
  }, [open])

  useEffect(() => {
    if (applySuccess) {
      setOtpOpen(true)
    }
  }, [applySuccess])

  return (
    <Modal
      open={open}
      withClose={true}
      onClose={onClose}
      withBackdrop={false}
      outsideClose={!verifySuccess}
      title={activeTitle}
    >
      <div tw='sm:w-[422px] w-[80vw]'>
        {cardLimit ? (
          <CardlimitContainer onClose={onClose} cardLimit={prepaidGeneralInfo?.data?.open_card_limit} />
        ) : !verifySuccess ? (
          otpOpen ? (
            <OtpVerify
              phone={watch('mobile_phone')}
              control={control}
              name='code'
              isOtpValid={isValid}
              isLoading={otpVerifyLoading}
              onOtpVerify={handleOtpVerify}
            />
          ) : (
            <div tw='flex flex-col gap-5 mt-5'>
              <MaskedInput
                control={control}
                name={'mobile_phone'}
                label={t('mobile_phone', { ns: 'auth' })}
                mask={'+7(999)999 99 99'}
                placeholder={'+7 ( _ _ _ ) _ _ _  _ _  _ _'}
              />
              <Button variant='gradient' loading={applyLoading} disabled={!isValid} onClick={handleSendOtp}>
                {t('receive_sms', { ns: 'auth' })}
              </Button>
              <SubBody twStyle={tw`text-secondary`}>
                <Trans
                  components={{
                    link1: (
                      <a
                        onClick={() =>
                          window.open(
                            'https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf',
                            '_self'
                          )
                        }
                        href='https://bankffin.kz/storage/docs/DW6oJlZFR8SQOYyRgJuIdKMYLXAj4M7qbhtMzdma.pdf'
                        tw='underline font-bold'
                      />
                    )
                  }}
                >
                  {t('agreement', {
                    ns: 'auth'
                  })}
                </Trans>
              </SubBody>
            </div>
          )
        ) : (
          <AcceptDenominationChoice onClose={onClose} denomination={denomination || { amount: '', product_code: '' }} />
        )}
      </div>
    </Modal>
  )
}

export default BuyCardModal
